/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../events';

const LOCAL_STORAGE_HISTORY_KEY = 'activity.nodals.ai';
const PRUNE_THRESHOLD_DAYS = 30;
const MAX_ENTRIES_PER_ACTIVITY = 30;

// Transient single-slot flag for accidental-click reconciliation. Held under a
// dedicated key, separate from the 30-day pruned history, because it has a very
// different lifecycle: one entry, ~10s lifetime, written before a redirect
// unloads the page and consumed (read-and-deleted) on the next page load.
const LOCAL_STORAGE_ACCIDENTAL_CLICK_KEY = 'aclk.nodals.ai';

interface HierarchyNode {
  id: string;
  children: HierarchyNode[];
}

/**
 * A click that was counted as an interaction just before the page navigated
 * away. Stored so that, if the user returns quickly, an accidental click can be
 * reconciled on the next page load. url pins the page the click happened on so a
 * return to a different page is not miscounted.
 */
interface PendingAccidentalClick {
  impressionId: string;
  deliveryId: string;
  url: string;
  ts: number;
}

interface ViewData {
  imps?: number[];
  views?: number[];
  ints?: number[];
  limpid?: string;
}

// Keys of ViewData that hold timestamp arrays (excludes string fields like limpid)
type ActivityArrayKey = 'imps' | 'views' | 'ints';

type ActivityData = Record<string, ViewData>;

interface ActivityEvents {
  ActivityRecorded: {
    entityId: string;
    activityType: 'imp' | 'view' | 'int';
    timestamp: number;
  };
}

class ActivityManager extends EventDispatcher<ActivityEvents> {
  private static instance: ActivityManager;
  private pageViewData: ActivityData = {};
  private cachedData: ActivityData | null = null;

  private constructor() {
    super();
  }

  public static getInstance(): ActivityManager {
    if (!ActivityManager.instance) {
      ActivityManager.instance = new ActivityManager();
    }
    return ActivityManager.instance;
  }

  /**
   * Persist a pending accidental-click flag. Must run synchronously inside the
   * click handler so the write commits before a redirect unloads the page.
   * Overwrites any existing flag — at most one click is in flight at a time.
   */
  public recordPendingAccidentalClick(flag: PendingAccidentalClick): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_ACCIDENTAL_CLICK_KEY, JSON.stringify(flag));
    } catch {
      // Storage unavailable (private mode, quota). The event is simply not
      // recorded — a tolerable miss, never a false positive.
    }
  }

  /**
   * Read and remove the pending accidental-click flag. Deleting before the
   * caller acts ensures a flag is reconciled at most once even if dispatch later
   * throws. Returns null when absent or unparseable.
   */
  public consumePendingAccidentalClick(): PendingAccidentalClick | null {
    let raw: string | null;
    try {
      raw = localStorage.getItem(LOCAL_STORAGE_ACCIDENTAL_CLICK_KEY);
      localStorage.removeItem(LOCAL_STORAGE_ACCIDENTAL_CLICK_KEY);
    } catch {
      return null;
    }

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.impressionId === 'string' &&
        typeof parsed.deliveryId === 'string' &&
        typeof parsed.url === 'string' &&
        typeof parsed.ts === 'number'
      ) {
        return parsed as PendingAccidentalClick;
      }
    } catch {
      // fall through
    }
    return null;
  }

  /**
   * Explicitly remove the pending flag. Used by the new-tab flow, where the
   * page does not unload and the accidental click is detected in-memory instead,
   * so any flag written by the click handler must not linger.
   */
  public clearPendingAccidentalClick(): void {
    try {
      localStorage.removeItem(LOCAL_STORAGE_ACCIDENTAL_CLICK_KEY);
    } catch {
      // nothing to do
    }
  }

  public recordImp(adId: string, impressionId?: string): void {
    this.recordActivity(adId, 'imps', 'imp', impressionId);
  }

  public recordView(adId: string): void {
    this.recordActivity(adId, 'views', 'view');
  }

  public recordInteraction(adId: string): void {
    this.recordActivity(adId, 'ints', 'int');
  }

  private recordActivity(
    adId: string,
    activityKey: ActivityArrayKey,
    activityType: 'imp' | 'view' | 'int',
    impressionId?: string,
  ): void {
    const now = Date.now();
    const stores = [
      { data: this.getData(), save: true },
      { data: this.pageViewData, save: false },
    ];

    // Record to both localStorage (historical) and pageViewData (in-memory)
    stores.forEach((store) => {
      if (!store.data[adId]) {
        store.data[adId] = {};
      }
      if (!store.data[adId][activityKey]) {
        store.data[adId][activityKey] = [];
      }
      store.data[adId][activityKey]!.push(now);

      // Cap at MAX_ENTRIES_PER_ACTIVITY, keeping the most recent
      if (store.data[adId][activityKey]!.length > MAX_ENTRIES_PER_ACTIVITY) {
        store.data[adId][activityKey] = store.data[adId][activityKey]!.slice(
          -MAX_ENTRIES_PER_ACTIVITY,
        );
      }

      if (store.save && activityType === 'imp' && impressionId) {
        store.data[adId].limpid = impressionId;
      }

      if (store.save) {
        this.save(store.data);
      }
    });

    this.dispatch('ActivityRecorded', {
      entityId: adId,
      activityType,
      timestamp: now,
    });
  }

  public getViewsForEntity(
    entityId: string,
    hierarchy: HierarchyNode,
    pageViewOnly: boolean = false,
  ): number[] {
    const adIds = ActivityManager.getAdIdsForEntity(entityId, hierarchy);

    if (pageViewOnly) {
      return adIds.flatMap((adId) => this.pageViewData[adId]?.views ?? []);
    }

    const data = this.getData();
    return adIds.flatMap((adId) => data[adId]?.views ?? []);
  }

  public getImpsForEntity(
    entityId: string,
    hierarchy: HierarchyNode,
    pageViewOnly: boolean = false,
  ): number[] {
    const adIds = ActivityManager.getAdIdsForEntity(entityId, hierarchy);

    if (pageViewOnly) {
      return adIds.flatMap((adId) => this.pageViewData[adId]?.imps ?? []);
    }

    const data = this.getData();
    return adIds.flatMap((adId) => data[adId]?.imps ?? []);
  }

  public getInteractionsForEntity(
    entityId: string,
    hierarchy: HierarchyNode,
    pageViewOnly: boolean = false,
  ): number[] {
    const adIds = ActivityManager.getAdIdsForEntity(entityId, hierarchy);

    if (pageViewOnly) {
      return adIds.flatMap((adId) => this.pageViewData[adId]?.ints ?? []);
    }

    const data = this.getData();
    return adIds.flatMap((adId) => data[adId]?.ints ?? []);
  }

  private static getAdIdsForEntity(entityId: string, root: HierarchyNode): string[] {
    const targetNode = ActivityManager.findNodeById(root, entityId);
    if (!targetNode) return [];

    const adIds: string[] = [];
    ActivityManager.collectLeafIds(targetNode, adIds);
    return adIds;
  }

  private static findNodeById(node: HierarchyNode, id: string): HierarchyNode | null {
    if (node.id === id) return node;

    for (const child of node.children) {
      const result = ActivityManager.findNodeById(child, id);
      if (result) return result;
    }

    return null;
  }

  private static collectLeafIds(node: HierarchyNode, adIds: string[]): void {
    if (node.children.length === 0) {
      adIds.push(node.id);
    } else {
      for (const child of node.children) {
        ActivityManager.collectLeafIds(child, adIds);
      }
    }
  }

  private getData(): ActivityData {
    if (this.cachedData !== null) {
      return this.cachedData;
    }

    const raw = localStorage.getItem(LOCAL_STORAGE_HISTORY_KEY);
    this.cachedData = {};

    if (!raw) {
      return this.cachedData;
    }

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) {
        return this.cachedData;
      }

      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'object' && value !== null) {
          const viewData: ViewData = {};
          if (
            Array.isArray((value as any).imps) &&
            (value as any).imps.every((item: any) => typeof item === 'number')
          ) {
            viewData.imps = (value as any).imps;
          }
          if (
            Array.isArray((value as any).views) &&
            (value as any).views.every((item: any) => typeof item === 'number')
          ) {
            viewData.views = (value as any).views;
          }
          if (
            Array.isArray((value as any).ints) &&
            (value as any).ints.every((item: any) => typeof item === 'number')
          ) {
            viewData.ints = (value as any).ints;
          }
          if (typeof (value as any).limpid === 'string') {
            viewData.limpid = (value as any).limpid;
          }
          if (viewData.imps || viewData.views || viewData.ints) {
            this.cachedData[key] = viewData;
          }
        }
      }

      // Prune old data once on initial load
      const [prunedData, hasChanges] = this.prune(this.cachedData);
      if (hasChanges) {
        this.save(prunedData);
      }
      this.cachedData = prunedData;

      return this.cachedData;
    } catch {
      return this.cachedData;
    }
  }

  private save(data: ActivityData): void {
    localStorage.setItem(LOCAL_STORAGE_HISTORY_KEY, JSON.stringify(data));
    this.cachedData = data;
  }

  private prune(data: ActivityData): [ActivityData, boolean] {
    const cutoff = Date.now() - PRUNE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    let hasChanges = false;

    const cleaned: ActivityData = {};

    for (const [adId, viewData] of Object.entries(data)) {
      const cleanedViewData: ViewData = {};

      if (viewData.imps) {
        let prunedImps = viewData.imps.filter((ts) => ts >= cutoff);
        if (prunedImps.length > MAX_ENTRIES_PER_ACTIVITY) {
          prunedImps = prunedImps.slice(-MAX_ENTRIES_PER_ACTIVITY);
        }
        if (prunedImps.length > 0) {
          cleanedViewData.imps = prunedImps;
        }
        if (prunedImps.length !== viewData.imps.length) {
          hasChanges = true;
        }
      }

      if (viewData.views) {
        let prunedViews = viewData.views.filter((ts) => ts >= cutoff);
        if (prunedViews.length > MAX_ENTRIES_PER_ACTIVITY) {
          prunedViews = prunedViews.slice(-MAX_ENTRIES_PER_ACTIVITY);
        }
        if (prunedViews.length > 0) {
          cleanedViewData.views = prunedViews;
        }
        if (prunedViews.length !== viewData.views.length) {
          hasChanges = true;
        }
      }

      if (viewData.ints) {
        let prunedInts = viewData.ints.filter((ts) => ts >= cutoff);
        if (prunedInts.length > MAX_ENTRIES_PER_ACTIVITY) {
          prunedInts = prunedInts.slice(-MAX_ENTRIES_PER_ACTIVITY);
        }
        if (prunedInts.length > 0) {
          cleanedViewData.ints = prunedInts;
        }
        if (prunedInts.length !== viewData.ints.length) {
          hasChanges = true;
        }
      }

      if (cleanedViewData.imps || cleanedViewData.views || cleanedViewData.ints) {
        if (viewData.limpid) {
          cleanedViewData.limpid = viewData.limpid;
        }
        cleaned[adId] = cleanedViewData;
      } else if (viewData.imps || viewData.views || viewData.ints) {
        hasChanges = true;
      }
    }

    return [cleaned, hasChanges];
  }
}

export { ActivityManager };
export type { HierarchyNode, PendingAccidentalClick };
